import { oc } from "@orpc/contract";
import { z } from "zod";
import { BackgroundFailure } from "./background-failure";
import { BusinessProfileStatus } from "./customer-account";
import { Cents } from "./money";
import { OrderEventAttention, PaymentStatus } from "./order";

/**
 * The start page's work queue (spec, user story 6 and "Startpagina"): per kind how many there are
 * and the first few, each with what the admin needs to link to where it is handled.
 */

/** How many items of each kind the queue lists; `total` counts them all. */
export const WORK_QUEUE_ITEMS = 5;

const group = <T extends z.ZodType>(item: T) => z.object({ total: z.number().int(), items: z.array(item) });

/** Paid (or on account, or partly refunded), not cancelled, no tracking code yet: oldest first. */
export const OrderToShip = z.object({
	orderId: z.string(),
	orderNumber: z.string(),
	name: z.string(),
	company: z.string().nullable(),
	placedAt: z.iso.datetime(),
	paymentStatus: PaymentStatus,
	totalIncl: Cents,
});
export type OrderToShip = z.infer<typeof OrderToShip>;

/** A business application waiting for a decision. */
export const PendingApplication = z.object({
	customerId: z.string(),
	name: z.string().nullable(),
	email: z.string(),
	companyName: z.string().nullable(),
	status: BusinessProfileStatus,
	createdAt: z.iso.datetime(),
});
export type PendingApplication = z.infer<typeof PendingApplication>;

/** An on-account invoice past its due date: the longest overdue first. */
export const OverdueInvoice = z.object({
	invoiceId: z.string(),
	number: z.string(),
	orderId: z.string(),
	customerName: z.string(),
	dueOn: z.string(),
	daysOverdue: z.number().int(),
	totalIncl: Cents,
	reminders: z.number().int(),
});
export type OverdueInvoice = z.infer<typeof OverdueInvoice>;

/** An active variant of an active product, low on stock (`low`) or out of it (`out`). */
export const StockAlert = z.object({
	variantId: z.string(),
	productName: z.string(),
	variantName: z.string(),
	sku: z.string(),
	quantity: z.number().int(),
	lowStockThreshold: z.number().int(),
});
export type StockAlert = z.infer<typeof StockAlert>;

/** An order event an employee must act on and has not marked handled: the longest waiting first. */
export const AttentionItem = z.object({
	eventId: z.string(),
	orderId: z.string(),
	orderNumber: z.string(),
	name: z.string(),
	attention: OrderEventAttention,
	message: z.string().nullable(),
	at: z.iso.datetime(),
});
export type AttentionItem = z.infer<typeof AttentionItem>;

/** A review waiting to be judged (`pending`): the oldest first. */
export const PendingReview = z.object({
	reviewId: z.string(),
	productName: z.string(),
	stars: z.number().int(),
	title: z.string().nullable(),
	firstName: z.string().nullable(),
	writtenAt: z.iso.datetime(),
});
export type PendingReview = z.infer<typeof PendingReview>;

export const WorkQueue = z.object({
	attention: group(AttentionItem),
	toShip: group(OrderToShip),
	applications: group(PendingApplication),
	overdueInvoices: group(OverdueInvoice).extend({ totalAmount: Cents }),
	outOfStock: group(StockAlert),
	lowStock: group(StockAlert),
	/** Background work that failed (registering, a reset, a confirmation link): the oldest first. */
	backgroundFailures: group(BackgroundFailure),
	/** Reviews to judge ("Reviews te beoordelen"). */
	reviews: group(PendingReview),
});
export type WorkQueue = z.infer<typeof WorkQueue>;

export const WorkQueueContract = {
	/** What needs doing now, per kind: the count and the first few. */
	get: oc.route({ method: "GET", path: "/admin/work-queue" }).output(WorkQueue),
};
